import * as swcHelpers from '@swc/helpers';
import React__default from 'react';
import classnames from 'classnames';
import { useIntl } from '@grandvisionhq/state';
import { FormContext } from '../Form/Form.js';

const validate = (value, validations)=>{
    if (!validations) {
        return true;
    }
    return validations.reduce((isValid, validator)=>validator(value) && isValid
    , true);
};
const DefaultError = /*#__PURE__*/ React__default.memo(({ message , id , className  })=>/*#__PURE__*/ React__default.createElement("p", {
        id: id,
        className: classnames('field__error', className)
    }, message)
);
DefaultError.displayName = 'Memo(DefaultError)';
/*
 * <Field /> should always be used within a <Form />
 */ const Field = (_param)=>{
    var { children , name , id , label , icon , required , disabled , placeholder , error , className , validations  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "children",
        "name",
        "id",
        "label",
        "icon",
        "required",
        "disabled",
        "placeholder",
        "error",
        "className",
        "validations"
    ]);
    const resolvedId = id || name;
    const { getLabel  } = useIntl();
    const context = React__default.useContext(FormContext);
    const state = context.getInitialState && context.getInitialState(name) || {
        name,
        value: ''
    };
    const deps = [
        state.value,
        state.isValid,
        state.isTouched
    ];
    const handleChange = React__default.useCallback((value)=>{
        if (state.readonly) {
            return;
        }
        const updatedState = swcHelpers.objectSpread({}, state, {
            value,
            isValid: validate(value, validations)
        });
        context.onChange && context.onChange(updatedState);
        rest.onChange && rest.onChange(updatedState);
    }, [
        ...deps,
        validations,
        context.onChange,
        rest.onChange
    ]);
    const handleBlur = React__default.useCallback((value)=>{
        if (state.readonly) {
            return;
        }
        const updatedState = swcHelpers.objectSpread({}, state, {
            value,
            isTouched: true
        });
        context.onBlur && context.onBlur(updatedState);
        rest.onBlur && rest.onBlur(updatedState);
    }, [
        ...deps,
        context.onBlur,
        rest.onBlur
    ]);
    // To validate initial state value from context, on initial render and a change of validations
    React__default.useEffect(()=>{
        if (!context.onChange) {
            return;
        }
        const newValidationResult = validate(state.value, validations);
        // if the result of the validation of the value is the same as in context, skip update
        if (state.isValid !== newValidationResult) {
            context.onChange(swcHelpers.objectSpread({}, state, {
                isValid: newValidationResult
            }));
        }
    }, [
        validations,
        state.value,
        state.isValid,
        context.onChange
    ]);
    const { isValid , isTouched , readonly  } = state;
    const shouldDisplayValid = isValid === true && isTouched === true && state.value !== '' && !readonly;
    const shouldDisplayError = isValid === false && isTouched === true && !readonly;
    const childClasses = classnames({
        _is_valid: shouldDisplayValid,
        _has_error: shouldDisplayError
    });
    const { showRequiredAndOptionalLabels  } = context;
    const displayRequiredLabel = showRequiredAndOptionalLabels && required;
    const displayOptionalLabel = showRequiredAndOptionalLabels && !required;
    const errorContent = React__default.useMemo(()=>typeof error === 'string' ? shouldDisplayError && /*#__PURE__*/ React__default.createElement(DefaultError, {
            id: `${resolvedId}_error`,
            message: error
        }) : error(shouldDisplayError)
    , [
        shouldDisplayError,
        resolvedId,
        error
    ]);
    return /*#__PURE__*/ React__default.createElement("div", {
        className: classnames('field', className)
    }, label && /*#__PURE__*/ React__default.createElement("div", {
        className: "field__label-wrapper"
    }, /*#__PURE__*/ React__default.createElement("label", {
        htmlFor: resolvedId,
        className: "field__label"
    }, label, displayRequiredLabel && /*#__PURE__*/ React__default.createElement("span", {
        className: "field__required-asterisk"
    }, "*")), displayOptionalLabel && /*#__PURE__*/ React__default.createElement("small", {
        className: "field__optional-tag"
    }, getLabel('common.optional'))), /*#__PURE__*/ React__default.createElement("div", {
        className: "field__content"
    }, React__default.Children.map(children, (child)=>{
        return /*#__PURE__*/ React__default.cloneElement(child, {
            name,
            id: resolvedId,
            value: state.value,
            onChange: handleChange,
            onBlur: handleBlur,
            className: childClasses,
            required,
            disabled,
            placeholder
        });
    })), errorContent);
};
var Field$1 = Field;

export { DefaultError, Field$1 as default };
//# sourceMappingURL=Field.js.map
